import { useEffect, useRef } from "react";
import "./AchievementsSection.css";
import {
    animate,
    useMotionValue,
    useTransform,
    motion,
    useInView,
} from "framer-motion";

const DURATION = 2.8;

const Y_LABELS = ["18,000", "16,000", "12,000", "8,000", "4,000", "0"];
const X_LABELS = ["2005", "2008", "2011", "2014", "2017", "2020", "2023", "2026"];

/* ==========================================================
   BLUE NETWORK CURSOR
   ========================================================== */

function AchievementCursorNetwork() {
    const canvasRef = useRef(null);
    const sectionRef = useRef(null);
    const animationRef = useRef(null);

    const nodesRef = useRef([]);

    const mouseRef = useRef({
        x: -1000,
        y: -1000,
        targetX: -1000,
        targetY: -1000,
        active: false,
    });

    useEffect(() => {
        const canvas = canvasRef.current;
        const network = sectionRef.current;

        if (!canvas || !network) return;

        const ctx = canvas.getContext("2d");

        if (!ctx) return;

        let width = 0;
        let height = 0;
        let dpr = Math.min(
            window.devicePixelRatio || 1,
            2
        );

        const createNodes = () => {
            const count = Math.max(
                35,
                Math.min(
                    60,
                    Math.floor(
                        (width * height) / 26000
                    )
                )
            );

            nodesRef.current = Array.from(
                { length: count },
                (_, index) => ({
                    x: Math.random() * width,
                    y: Math.random() * height,

                    baseX:
                        Math.random() * width,

                    baseY:
                        Math.random() * height,

                    radius:
                        index % 5 === 0
                            ? 2.4
                            : 1.3,

                    phase:
                        Math.random() *
                        Math.PI *
                        2,

                    speed:
                        0.00035 +
                        Math.random() * 0.0005,
                })
            );
        };

        const resize = () => {
            const rect =
                network.getBoundingClientRect();

            width = rect.width;
            height = rect.height;

            dpr = Math.min(
                window.devicePixelRatio || 1,
                2
            );

            canvas.width =
                Math.floor(width * dpr);

            canvas.height =
                Math.floor(height * dpr);

            canvas.style.width =
                `${width}px`;

            canvas.style.height =
                `${height}px`;

            ctx.setTransform(
                dpr,
                0,
                0,
                dpr,
                0,
                0
            );

            createNodes();
        };

        const handleMouseMove = (event) => {
            const rect =
                network.getBoundingClientRect();

            const mouseX =
                event.clientX - rect.left;

            const mouseY =
                event.clientY - rect.top;

            const inside =
                mouseX >= 0 &&
                mouseX <= width &&
                mouseY >= 0 &&
                mouseY <= height;

            if (!inside) {
                mouseRef.current.active =
                    false;

                return;
            }

            mouseRef.current.targetX =
                mouseX;

            mouseRef.current.targetY =
                mouseY;

            mouseRef.current.active =
                true;
        };

        const handleMouseLeave = () => {
            mouseRef.current.active =
                false;

            mouseRef.current.targetX =
                -1000;

            mouseRef.current.targetY =
                -1000;
        };

        const draw = (time) => {
            ctx.clearRect(
                0,
                0,
                width,
                height
            );

            const mouse =
                mouseRef.current;

            // Smooth cursor movement
            mouse.x +=
                (mouse.targetX - mouse.x) *
                0.08;

            mouse.y +=
                (mouse.targetY - mouse.y) *
                0.08;

            const nodes =
                nodesRef.current;

            // Gentle background node movement
            nodes.forEach((node) => {
                const driftX =
                    Math.sin(
                        time *
                        node.speed +
                        node.phase
                    ) * 10;

                const driftY =
                    Math.cos(
                        time *
                        node.speed *
                        0.85 +
                        node.phase
                    ) * 8;

                node.x +=
                    (
                        node.baseX +
                        driftX -
                        node.x
                    ) * 0.012;

                node.y +=
                    (
                        node.baseY +
                        driftY -
                        node.y
                    ) * 0.012;
            });

            const cursorDistance = 210;
            const nodeDistance = 145;

            /*
             * BACKGROUND NETWORK
             */

            for (
                let i = 0;
                i < nodes.length;
                i++
            ) {
                const a = nodes[i];

                for (
                    let j = i + 1;
                    j < nodes.length;
                    j++
                ) {
                    const b = nodes[j];

                    const dx =
                        a.x - b.x;

                    const dy =
                        a.y - b.y;

                    const distance =
                        Math.sqrt(
                            dx * dx +
                            dy * dy
                        );

                    if (
                        distance >
                        nodeDistance
                    ) {
                        continue;
                    }

                    const opacity =
                        (
                            1 -
                            distance /
                                nodeDistance
                        ) * 0.13;

                    ctx.beginPath();

                    ctx.moveTo(
                        a.x,
                        a.y
                    );

                    ctx.lineTo(
                        b.x,
                        b.y
                    );

                    ctx.strokeStyle =
                        `rgba(80, 145, 255, ${opacity})`;

                    ctx.lineWidth = 0.8;

                    ctx.stroke();
                }
            }

            /*
             * CURSOR INTERACTION
             */

            if (mouse.active) {
                nodes.forEach((node) => {
                    const dx =
                        node.x -
                        mouse.x;

                    const dy =
                        node.y -
                        mouse.y;

                    const distance =
                        Math.sqrt(
                            dx * dx +
                            dy * dy
                        );

                    if (
                        distance >
                        cursorDistance
                    ) {
                        return;
                    }

                    const influence =
                        1 -
                        distance /
                            cursorDistance;

                    /*
                     * Cursor → node line
                     */

                    ctx.beginPath();

                    ctx.moveTo(
                        mouse.x,
                        mouse.y
                    );

                    ctx.lineTo(
                        node.x,
                        node.y
                    );

                    ctx.strokeStyle =
                        `rgba(
                            75,
                            165,
                            255,
                            ${influence * 0.65}
                        )`;

                    ctx.lineWidth =
                        0.8 +
                        influence * 0.7;

                    ctx.stroke();

                    /*
                     * Small glow around
                     * nearby nodes
                     */

                    ctx.beginPath();

                    ctx.arc(
                        node.x,
                        node.y,
                        node.radius +
                            influence * 2,
                        0,
                        Math.PI * 2
                    );

                    ctx.fillStyle =
                        `rgba(
                            100,
                            190,
                            255,
                            ${0.35 +
                            influence * 0.55}
                        )`;

                    ctx.shadowColor =
                        "rgba(65, 160, 255, 0.9)";

                    ctx.shadowBlur =
                        10 +
                        influence * 15;

                    ctx.fill();

                    ctx.shadowBlur = 0;
                });

                /*
                 * CURSOR HALO
                 */

                const halo =
                    ctx.createRadialGradient(
                        mouse.x,
                        mouse.y,
                        0,
                        mouse.x,
                        mouse.y,
                        130
                    );

                halo.addColorStop(
                    0,
                    "rgba(50, 145, 255, 0.18)"
                );

                halo.addColorStop(
                    0.45,
                    "rgba(50, 145, 255, 0.07)"
                );

                halo.addColorStop(
                    1,
                    "rgba(50, 145, 255, 0)"
                );

                ctx.beginPath();

                ctx.arc(
                    mouse.x,
                    mouse.y,
                    130,
                    0,
                    Math.PI * 2
                );

                ctx.fillStyle = halo;

                ctx.fill();

                /*
                 * MAIN CURSOR NODE
                 */

                ctx.beginPath();

                ctx.arc(
                    mouse.x,
                    mouse.y,
                    4,
                    0,
                    Math.PI * 2
                );

                ctx.fillStyle =
                    "rgba(115, 205, 255, 1)";

                ctx.shadowColor =
                    "rgba(55, 155, 255, 1)";

                ctx.shadowBlur = 22;

                ctx.fill();

                ctx.shadowBlur = 0;
            }

            /*
             * BACKGROUND NODES
             */

            nodes.forEach((node) => {
                const dx =
                    node.x -
                    mouse.x;

                const dy =
                    node.y -
                    mouse.y;

                const distance =
                    Math.sqrt(
                        dx * dx +
                        dy * dy
                    );

                const influence =
                    mouse.active &&
                    distance <
                        cursorDistance
                        ? 1 -
                          distance /
                              cursorDistance
                        : 0;

                const radius =
                    node.radius +
                    influence * 1.8;

                ctx.beginPath();

                ctx.arc(
                    node.x,
                    node.y,
                    radius,
                    0,
                    Math.PI * 2
                );

                ctx.fillStyle =
                    `rgba(
                        85,
                        170,
                        255,
                        ${0.3 +
                        influence * 0.6}
                    )`;

                ctx.shadowColor =
                    "rgba(60, 155, 255, 0.8)";

                ctx.shadowBlur =
                    influence > 0
                        ? 14
                        : 5;

                ctx.fill();

                ctx.shadowBlur = 0;
            });

            animationRef.current =
                requestAnimationFrame(draw);
        };

        resize();

        // IMPORTANT:
        // Listen on window instead of the
        // pointer-events:none canvas layer.
        window.addEventListener(
            "mousemove",
            handleMouseMove
        );

        window.addEventListener(
            "resize",
            resize
        );

        animationRef.current =
            requestAnimationFrame(draw);

        return () => {
            window.removeEventListener(
                "mousemove",
                handleMouseMove
            );

            window.removeEventListener(
                "resize",
                resize
            );

            handleMouseLeave();

            if (
                animationRef.current
            ) {
                cancelAnimationFrame(
                    animationRef.current
                );
            }
        };
    }, []);

    return (
        <div
            ref={sectionRef}
            className="achievement-cursor-network"
            aria-hidden="true"
        >
            <canvas ref={canvasRef} />
        </div>
    );
}
/* ==========================================================
   MAIN SECTION
   ========================================================== */

function AchievementsSection() {
    const chartRef = useRef(null);

    const isInView = useInView(chartRef, {
        once: true,
        amount: 0.4,
    });

    return (
        <section className="achievement-section">
            <AchievementCursorNetwork />

            <div className="achievement-bg-grid"></div>
            <div className="achievement-bg-glow"></div>

            <div className="achievement-container">
                {/* HEADER */}
                <div className="achievement-header">
                    <p className="achievement-tag">
                        ACHIEVEMENTS
                    </p>

                    <p className="achievement-subheading">
                        Engineering Excellence Built Over
                        <span> 14 Years</span>
                    </p>
                </div>

                {/* TOP CONTENT */}
                <div className="achievement-showcase">
                    {/* LEFT */}
                    <div className="achievement-info">
                        <h2 className="achievement-heading">
                            Grid Compliance Study
                        </h2>

                        <p className="achievement-summary">
                            Grid Compliance study experience of IPR
                            Technologies has successfully crossed more
                            than <strong>18,000 MW</strong>, delivering
                            reliable power system consultancy and
                            renewable energy integration projects across
                            India.
                        </p>
                    </div>

                    {/* RIGHT */}
                    <div
                        className="achievement-chart"
                        ref={chartRef}
                    >
                        <div className="achievement-chart-label">
                            TOTAL ACCUMULATED CAPACITY
                        </div>

                        <AchievementCounter
                            isInView={isInView}
                        />

                        <div className="achievement-chart-frame">
                            <div className="achievement-svg-wrap">
                                <svg
                                    className="achievement-chart-svg"
                                    viewBox="0 0 900 400"
                                    preserveAspectRatio="none"
                                >
                                    <defs>
                                        <linearGradient
                                            id="achievementGradient"
                                            x1="0%"
                                            y1="100%"
                                            x2="100%"
                                            y2="0%"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor="#1E5EFF"
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor="#67C8FF"
                                            />
                                        </linearGradient>

                                        <filter
                                            id="achievementGlow"
                                            x="-50%"
                                            y="-50%"
                                            width="200%"
                                            height="200%"
                                        >
                                            <feGaussianBlur
                                                stdDeviation="5"
                                                result="blur"
                                            />

                                            <feMerge>
                                                <feMergeNode in="blur" />
                                                <feMergeNode in="SourceGraphic" />
                                            </feMerge>
                                        </filter>
                                    </defs>

                                    <motion.path
                                        d="
                                            M0 360
                                            C140 320 260 280 360 240
                                            C460 200 540 165 630 120
                                            C710 82 790 45 880 12
                                        "
                                        fill="none"
                                        stroke="url(#achievementGradient)"
                                        strokeWidth="6"
                                        strokeLinecap="round"
                                        filter="url(#achievementGlow)"
                                        initial={{
                                            pathLength: 0,
                                        }}
                                        animate={
                                            isInView
                                                ? {
                                                    pathLength: 1,
                                                }
                                                : {}
                                        }
                                        transition={{
                                            duration: DURATION,
                                            ease: "easeInOut",
                                        }}
                                    />

                                    {[
                                        {
                                            cx: 0,
                                            cy: 360,
                                            r: 5,
                                            delay: 0.2,
                                        },
                                        {
                                            cx: 360,
                                            cy: 240,
                                            r: 5,
                                            delay: 0.7,
                                        },
                                        {
                                            cx: 630,
                                            cy: 120,
                                            r: 6,
                                            delay: 1.4,
                                        },
                                        {
                                            cx: 880,
                                            cy: 12,
                                            r: 7,
                                            delay: 2.2,
                                        },
                                    ].map((p, i) => (
                                        <motion.circle
                                            key={i}
                                            cx={p.cx}
                                            cy={p.cy}
                                            r={p.r}
                                            fill="#66B6FF"
                                            filter="url(#achievementGlow)"
                                            initial={{
                                                scale: 0,
                                            }}
                                            animate={
                                                isInView
                                                    ? {
                                                        scale: 1,
                                                    }
                                                    : {}
                                            }
                                            transition={{
                                                delay: p.delay,
                                            }}
                                        />
                                    ))}
                                </svg>
                            </div>

                            <div className="achievement-yaxis">
                                {Y_LABELS.map((label) => (
                                    <span key={label}>
                                        {label}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="achievement-xaxis">
                            {X_LABELS.map((label) => (
                                <span key={label}>
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ==========================================================
   OPTIONAL REUSABLE COUNTER
   ========================================================== */

function AnimatedValue({
    value,
    unit,
    isInView,
}) {
    const count = useMotionValue(0);

    const rounded = useTransform(
        count,
        (latest) =>
            Math.floor(latest).toLocaleString()
    );

    useEffect(() => {
        if (!isInView) return;

        const numericValue =
            typeof value === "string"
                ? Number(
                    value
                        .replace(/,/g, "")
                        .replace("+", "")
                )
                : value;

        const controls = animate(
            count,
            numericValue,
            {
                duration: 2.3,
                ease: "easeOut",
            }
        );

        return () => controls.stop();
    }, [isInView, value, count]);

    return (
        <>
            <motion.span>
                {rounded}
            </motion.span>

            <small>
                {unit === "Projects" ? "+" : ""}
                {" "}
                {unit}
            </small>
        </>
    );
}

/* ==========================================================
   MAIN ACHIEVEMENT COUNTER
   ========================================================== */

function AchievementCounter({
    isInView,
}) {
    const count = useMotionValue(0);

    const rounded = useTransform(
        count,
        (latest) =>
            Math.floor(latest).toLocaleString()
    );

    useEffect(() => {
        if (!isInView) return;

        const controls = animate(
            count,
            18000,
            {
                duration: DURATION,
                ease: "easeOut",
            }
        );

        return () => controls.stop();
    }, [isInView, count]);

    return (
        <div className="achievement-number-top">
            <motion.span>
                {rounded}
            </motion.span>

            <span className="achievement-unit">
                MW
            </span>
        </div>
    );
}

export default AchievementsSection;